import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { dashboardConfigApi } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import {
  Search,
  Plus,
  Check,
} from 'lucide-react'
import type { WidgetDefinition, WidgetCategory, WidgetConfig, WidgetType } from '@/types'
import { WIDGET_COMPONENTS } from './widgets'

interface AddWidgetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AddWidgetDialog({ open, onOpenChange }: AddWidgetDialogProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<WidgetCategory | 'All'>('All')

  // Fetch available widgets
  const { data: availableWidgets = [], isLoading } = useQuery({
    queryKey: ['available-widgets'],
    queryFn: dashboardConfigApi.getAvailableWidgets,
    enabled: open,
  })

  // Fetch current config to check which widgets are already added
  const { data: currentConfig } = useQuery({
    queryKey: ['dashboard-config'],
    queryFn: dashboardConfigApi.getConfig,
    enabled: open,
  })

  // Add widget mutation
  const addWidgetMutation = useMutation({
    mutationFn: async (widget: WidgetDefinition) => {
      // Get the latest config from the query cache to avoid stale data
      const latestConfig = queryClient.getQueryData<{ widgets: WidgetConfig[] }>(['dashboard-config'])
      const currentWidgets = latestConfig?.widgets || currentConfig?.widgets || []

      // Generate unique ID
      const newId = `w${Date.now()}`

      // Find the next available Y position
      const maxY = currentWidgets.reduce((max, w) => {
        const bottom = w.position.y + w.size.height
        return bottom > max ? bottom : max
      }, 0)

      const newWidget: WidgetConfig = {
        id: newId,
        type: widget.type,
        position: { x: 0, y: maxY },
        size: widget.default_size,
        visible: true,
        settings: {},
      }

      return dashboardConfigApi.saveConfig({
        widgets: [...currentWidgets, newWidget],
      })
    },
    onSuccess: (data) => {
      // Directly update the cache with the new data to ensure immediate UI update
      queryClient.setQueryData(['dashboard-config'], data)
      toast.success(t('dashboard.widgetAdded'))
      // Keep dialog open to allow adding multiple widgets
    },
    onError: () => {
      toast.error(t('dashboard.widgetAddError'))
    },
  })

  // Filter widgets
  const filteredWidgets = useMemo(() => {
    return availableWidgets.filter(widget => {
      const matchesSearch = widget.name.toLowerCase().includes(search.toLowerCase()) ||
        widget.description.toLowerCase().includes(search.toLowerCase())
      const matchesCategory = selectedCategory === 'All' || widget.category === selectedCategory
      return matchesSearch && matchesCategory
    })
  }, [availableWidgets, search, selectedCategory])

  // Group widgets by category
  const groupedWidgets = useMemo(() => {
    const groups: Record<string, WidgetDefinition[]> = {}
    filteredWidgets.forEach(widget => {
      if (!groups[widget.category]) {
        groups[widget.category] = []
      }
      groups[widget.category].push(widget)
    })
    return groups
  }, [filteredWidgets])

  // Get unique categories
  const categories: (WidgetCategory | 'All')[] = useMemo(() => {
    const cats = [...new Set(availableWidgets.map(w => w.category))] as WidgetCategory[]
    return ['All', ...cats]
  }, [availableWidgets])

  // Check if widget is already added
  const isWidgetAdded = (widgetType: string) => {
    return currentConfig?.widgets.some(w => w.type === widgetType && w.visible)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl !grid-rows-[auto_auto_auto_1fr] max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>{t('dashboard.addWidget')}</DialogTitle>
          <DialogDescription>{t('dashboard.selectWidgetToAdd')}</DialogDescription>
        </DialogHeader>

        {/* Search and Filter */}
        <div className="flex gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('dashboard.searchWidgets')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap">
          {categories.map(category => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category === 'All' ? t('common.all') : category}
            </Button>
          ))}
        </div>

        {/* Widget List */}
        <div className="overflow-y-auto min-h-0 pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
          ) : Object.keys(groupedWidgets).length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-muted-foreground">{t('dashboard.noWidgetsFound')}</div>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedWidgets).map(([category, widgets]) => (
                <div key={category}>
                  <h3 className="font-semibold mb-3">{category}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {widgets.map(widget => {
                      const WidgetComponent = WIDGET_COMPONENTS[widget.type as WidgetType]
                      const added = isWidgetAdded(widget.type)

                      return (
                        <div
                          key={widget.type}
                          className={`relative border rounded-lg overflow-hidden ${added ? 'opacity-60' : 'hover:border-primary'}`}
                        >
                          {/* Widget Preview */}
                          <div className="h-[160px] overflow-hidden pointer-events-none relative">
                            <div
                              className="absolute inset-0 origin-top-left"
                              style={{ transform: 'scale(0.75)', width: '133.33%', height: '133.33%' }}
                            >
                              {WidgetComponent ? <WidgetComponent /> : (
                                <div className="h-full bg-muted/50 flex items-center justify-center">
                                  <span className="text-muted-foreground text-sm">{widget.name}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Add Button Overlay */}
                          <div className="absolute bottom-2 right-2">
                            <Button
                              size="sm"
                              variant={added ? 'secondary' : 'default'}
                              disabled={added || addWidgetMutation.isPending}
                              onClick={() => addWidgetMutation.mutate(widget)}
                              className="shadow-md"
                            >
                              {added ? (
                                <>
                                  <Check className="h-4 w-4 mr-1" />
                                  {t('dashboard.added')}
                                </>
                              ) : (
                                <>
                                  <Plus className="h-4 w-4 mr-1" />
                                  {t('common.add')}
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

